/**
 * EqualOppsSection — full UK Equal Opportunities + Rare-style contextual data.
 *
 * Structure mirrors what UK employers (and Rare Recruitment, the Social Mobility
 * Commission, major grad schemes) actually ask for. Grouped into 10 collapsible
 * categories. All fields optional, all "Prefer not to say"-friendly. Always
 * inline-editable — debounced auto-save, plus an explicit "lock it in" button
 * at the bottom that confirms the whole questionnaire.
 *
 * Storage: same /api/diversity endpoint, expanded DiversityIn model in
 * /app/backend/models.py.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  UsersThree, CaretDown, CaretUp, LockKey, Globe, IdentificationBadge, Person,
  Heart, RainbowCloud, BookOpen, Tree, ShieldStar, Star,
} from "@phosphor-icons/react";

// ---------- Helpers ----------
function useDebounced(fn, delay = 800) {
  const ref = useRef(fn);
  const timer = useRef(null);
  useEffect(() => { ref.current = fn; }, [fn]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const debounced = (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(...args), delay);
  };
  debounced.cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return debounced;
}

// ---------- Choice lists ----------
const PNTS = "Prefer not to say";
const yes_no = ["", "Yes", "No", PNTS];
const yes_no_dontknow = ["", "Yes", "No", "Don't know", PNTS];

const ETHNICITY = [
  "",
  // White
  "White: British / English / Scottish / Welsh / Northern Irish",
  "White: Irish",
  "White: Gypsy or Irish Traveller",
  "White: Any other white background",
  // Mixed
  "Mixed: White & Black Caribbean",
  "Mixed: White & Black African",
  "Mixed: White & Asian",
  "Mixed: Any other mixed background",
  // Asian
  "Asian: Indian",
  "Asian: Pakistani",
  "Asian: Bangladeshi",
  "Asian: Chinese",
  "Asian: Any other Asian background",
  // Black
  "Black: African",
  "Black: Caribbean",
  "Black: Any other Black background",
  // Other
  "Arab",
  "Any other ethnic group",
  PNTS,
];

const RELIGION = ["", "No religion", "Christian", "Buddhist", "Hindu", "Jewish", "Muslim", "Sikh", "Any other religion", PNTS];

const SCHOOL_TYPE = [
  "",
  "State school (non-selective)",
  "State school (grammar / selective)",
  "Independent / fee-paying",
  "Independent with bursary or scholarship",
  "Attended school outside the UK",
  "Other",
  PNTS,
];

const PRIMARY_EARNER_OCCUPATION = [
  "",
  "Modern professional & traditional professional (e.g. teacher, doctor, lawyer)",
  "Senior managers and administrators (e.g. CEO, civil servant)",
  "Clerical and intermediate (e.g. secretary, nursery nurse)",
  "Technical and craft (e.g. plumber, electrician)",
  "Routine and semi-routine (e.g. driver, cleaner)",
  "Long-term unemployed or never worked",
  "Retired",
  "Other",
  "Don't know",
  PNTS,
];

const INCOME_BAND = [
  "",
  "Under £15,000",
  "£15,000 – £29,999",
  "£30,000 – £49,999",
  "£50,000 – £74,999",
  "£75,000 – £99,999",
  "£100,000 +",
  "Don't know",
  PNTS,
];

const CLASS_SELF = [
  "",
  "Working class",
  "Lower middle class",
  "Middle class",
  "Upper middle class",
  "Upper class",
  "Don't think of myself this way",
  PNTS,
];

const SEXUAL_ORIENTATION = ["", "Heterosexual / Straight", "Gay or Lesbian", "Bisexual", "Asexual", "Prefer to self-describe", PNTS];
const GENDER = ["", "Woman", "Man", "Non-binary", "Prefer to self-describe", PNTS];
const SPONSORSHIP = ["", "No — already eligible to work", "Yes — now", "Yes — in the future", PNTS];
const POLAR_QUINTILES = ["", "Q1 (lowest progression)", "Q2", "Q3", "Q4", "Q5 (highest progression)", "Don't know"];
const IMD_DECILES = ["", ...Array.from({ length: 10 }, (_, i) => `Decile ${i + 1}${i === 0 ? " (most deprived)" : i === 9 ? " (least deprived)" : ""}`), "Don't know"];

// ---------- Groups ----------
// Each group is a card; the icon's `color` becomes a subtle left-stripe so the
// long form is scannable. All fields render as either <select> or <input>.
const GROUPS = [
  {
    key: "eligibility",
    label: "Legal & eligibility",
    icon: IdentificationBadge,
    stripe: "var(--brand-primary)",
    fields: [
      ["right_to_work_status", "Right-to-work status", "input", null, "e.g. British citizen, Settled status, Skilled Worker visa"],
      ["needs_sponsorship", "Will you need visa sponsorship?", "select", SPONSORSHIP],
      ["nationality", "Nationality", "input"],
      ["country_of_birth", "Country of birth", "input"],
      ["postcode_growing_up", "Postcode where you grew up", "input", null, "used for socio-economic / Rare context only"],
      ["date_of_birth", "Date of birth", "date"],
    ],
  },
  {
    key: "gender",
    label: "Gender & identity",
    icon: Person,
    stripe: "var(--bolt-sage)",
    fields: [
      ["gender", "Gender", "select", GENDER],
      ["gender_identity_matches_birth", "Is your gender identity the same as the sex you were registered with at birth?", "select", yes_no],
      ["transgender", "Do you identify as transgender?", "select", yes_no],
      ["gender_identity_self_describe", "Self-describe your gender identity (optional)", "input"],
    ],
  },
  {
    key: "ethnicity",
    label: "Ethnicity",
    icon: UsersThree,
    stripe: "var(--brand-primary)",
    fields: [
      ["ethnicity", "Ethnic background", "select", ETHNICITY],
      ["ethnicity_self_describe", "Self-describe (optional)", "input"],
    ],
  },
  {
    key: "disability",
    label: "Disability & health",
    icon: Heart,
    stripe: "var(--bolt-sage)",
    fields: [
      ["disability", "Do you consider yourself to have a disability under the Equality Act 2010?", "select", yes_no],
      ["long_term_health_conditions", "Do you have any long-term health conditions?", "select", yes_no],
      ["requires_adjustments", "Do you require reasonable adjustments during the recruitment process?", "select", yes_no],
      ["adjustment_details", "What adjustments would support you?", "textarea", null, "e.g. interview format, extra time, written instructions"],
    ],
  },
  {
    key: "sexual_orientation",
    label: "Sexual orientation",
    icon: RainbowCloud,
    stripe: "var(--brand-primary)",
    fields: [
      ["sexual_orientation", "Sexual orientation", "select", SEXUAL_ORIENTATION],
      ["sexual_orientation_self_describe", "Self-describe (optional)", "input"],
    ],
  },
  {
    key: "religion",
    label: "Religion or belief",
    icon: Star,
    stripe: "var(--bolt-sage)",
    fields: [
      ["religion", "Religion or belief", "select", RELIGION],
      ["religion_self_describe", "Self-describe (optional)", "input"],
    ],
  },
  {
    key: "socio_economic",
    label: "Socio-economic background",
    icon: BookOpen,
    stripe: "var(--brand-primary)",
    fields: [
      ["parental_university", "Did either of your parents complete a university degree?", "select", yes_no_dontknow],
      ["school_type_11_to_16", "What type of school did you mainly attend between ages 11–16?", "select", SCHOOL_TYPE],
      ["free_school_meals", "Were you eligible for free school meals at any point during your education?", "select", yes_no_dontknow],
      ["primary_earner_occupation_at_14", "At age 14, what was the occupation of your main household earner?", "select", PRIMARY_EARNER_OCCUPATION],
      ["care_experience", "Have you ever been in care or a looked-after child?", "select", yes_no],
      ["first_gen_university", "Are you the first in your family to attend university?", "select", ["", "Yes", "No", "Not applicable", PNTS]],
      ["carer_responsibilities", "Do you currently have caring responsibilities for a family member?", "select", yes_no],
      ["refugee_or_asylum", "Have you ever held refugee or asylum-seeker status?", "select", yes_no],
      ["household_income_band", "What was your household income growing up? (optional)", "select", INCOME_BAND],
    ],
  },
  {
    key: "rare_contextual",
    label: "Rare contextual data (advanced — optional)",
    icon: Tree,
    stripe: "var(--bolt-sage)",
    fields: [
      ["polar4_quintile", "POLAR4 quintile (postcode-based university participation)", "select", POLAR_QUINTILES, "look this up on the OfS POLAR tool"],
      ["imd_decile", "Index of Multiple Deprivation (IMD) decile", "select", IMD_DECILES],
      ["school_attainment_band", "Your school's attainment band (GCSE/A-Level)", "select", ["", "Top 20%", "21–40%", "41–60%", "61–80%", "Bottom 20%", "Don't know"]],
      ["attended_low_attainment_school", "Did you attend a school in the bottom 40% of UK attainment?", "select", yes_no_dontknow],
      ["lived_in_low_progression_area", "Did you live in an area with low progression to higher education?", "select", yes_no_dontknow],
      ["education_disruption", "Did you experience disruption during your education? (illness, caring, bereavement, displacement)", "select", yes_no],
      ["education_disruption_details", "Details (optional)", "textarea"],
    ],
  },
  {
    key: "veteran",
    label: "Veteran / Armed Forces",
    icon: ShieldStar,
    stripe: "var(--brand-primary)",
    fields: [
      ["veteran_status", "Have you served in the UK Armed Forces?", "select", yes_no],
      ["reservist_status", "Are you currently a reservist?", "select", yes_no],
      ["armed_forces_dependant", "Are you a dependant of someone who served?", "select", yes_no],
    ],
  },
  {
    key: "additional",
    label: "Additional optional questions",
    icon: Globe,
    stripe: "var(--bolt-sage)",
    fields: [
      ["marital_status", "Marital status", "select", ["", "Single", "Married / Civil partnership", "Separated", "Divorced", "Widowed", PNTS]],
      ["pregnancy_maternity", "Pregnancy / maternity (monitoring only)", "select", ["", "Currently pregnant", "On maternity leave", "Recently returned from maternity leave", "Not applicable", PNTS]],
      ["language_proficiency", "Languages you speak fluently (besides English)", "input", null, "comma-separated"],
      ["class_self_assessment", "How would you describe your social class?", "select", CLASS_SELF],
      ["digital_access", "Did you have reliable internet & a laptop at home growing up?", "select", yes_no],
    ],
  },
];

// ---------- Field renderer ----------
function Field({ k, label, type, options, hint, value, onChange }) {
  return (
    <label className="block" data-testid={`eo-field-${k}`}>
      <span className="label-tiny block mb-1">{label}</span>
      {type === "select" ? (
        <select value={value || ""}
          data-testid={`eo-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm">
          {options.map((o) => <option key={o} value={o}>{o || "— select —"}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea value={value || ""}
          data-testid={`eo-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hint || ""}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm min-h-[80px]"/>
      ) : type === "date" ? (
        <input type="date" value={value || ""}
          data-testid={`eo-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
      ) : (
        <input value={value || ""}
          data-testid={`eo-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hint || ""}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
      )}
      {hint && type !== "input" && type !== "textarea" && (
        <p className="font-mono-d text-[10px] text-[var(--brand-muted)] mt-1">{hint}</p>
      )}
    </label>
  );
}

// ---------- Group card ----------
function GroupCard({ group, form, onChange, collapsed, onToggleCollapse }) {
  const Icon = group.icon;
  return (
    <div className="card-hard rounded-md bg-white overflow-hidden relative" data-testid={`eo-group-${group.key}`}>
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: group.stripe }}/>
      <button onClick={onToggleCollapse}
        data-testid={`eo-group-toggle-${group.key}`}
        className="w-full flex items-center justify-between gap-3 p-3 pl-5 text-left hover:bg-[var(--brand-bg)] transition">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon size={16} weight="duotone" style={{ color: group.stripe }}/>
          <p className="font-medium text-sm">{group.label}</p>
        </div>
        {collapsed ? <CaretDown size={16} weight="bold"/> : <CaretUp size={16} weight="bold"/>}
      </button>
      {!collapsed && (
        <div className="px-5 pb-5 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
          {group.fields.map(([k, label, type, options, hint]) => (
            <Field key={k} k={k} label={label} type={type} options={options} hint={hint}
              value={form[k]}
              onChange={(v) => onChange(k, v)}/>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Section ----------
export default function EqualOppsSection() {
  const [form, setForm] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set(GROUPS.slice(2).map((g) => g.key)));  // collapse all but first 2 by default
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    api.get("/diversity").then(({ data }) => setForm(data || {})).catch(() => setForm({}));
  }, []);

  const persist = useDebounced(async (next) => {
    setSaving(true);
    try {
      await api.put("/diversity", next);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  }, 800);

  const onChange = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    setDirty(true);
    persist(next);
  };

  const toggleGroup = (key) => {
    setCollapsedGroups((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => setCollapsedGroups(new Set());
  const collapseAll = () => setCollapsedGroups(new Set(GROUPS.map((g) => g.key)));

  const lockIn = async () => {
    persist.cancel?.();
    setSaving(true);
    try {
      await api.put("/diversity", form);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      toast.success("Locked into the vault");
      collapseAll();
    } catch { toast.error("Couldn't lock it in — try again"); }
    finally { setSaving(false); }
  };

  if (form === null) return null;

  const totalFilled = Object.values(form).filter((v) => v && v.toString().trim() !== "").length;
  const anyCollapsed = collapsedGroups.size > 0;

  return (
    <section className="space-y-4" data-testid="section-equal-opps">
      <div className="card-hard rounded-md bg-white p-5 relative overflow-hidden">
        <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--brand-primary)]"/>
        <div className="flex items-start justify-between gap-3 pl-2">
          <div>
            <p className="label-tiny flex items-center gap-1.5 m-0">
              <UsersThree size={12} weight="duotone" className="text-[var(--brand-primary)]"/> /equal opportunities
            </p>
            <h2 className="font-display text-2xl tracking-tight mt-1">Diversity questionnaire</h2>
            <p className="text-xs text-[var(--brand-muted)] mt-1 max-w-2xl">
              the full UK equal-opps + Rare-style contextual question set. answer what you&apos;re comfortable with — every question is optional and &ldquo;prefer not to say&rdquo; is always valid. changes save automatically. special-category data: never on share links, never in AI.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className="font-mono-d text-xs text-[var(--brand-muted)]">
              {saving ? "saving…" : dirty ? "unsaved" : justSaved ? <span className="text-[var(--bolt-sage)]">saved</span> : `${totalFilled} answered`}
            </span>
            <button onClick={anyCollapsed ? expandAll : collapseAll}
              data-testid="eo-collapse-all"
              className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
              {anyCollapsed ? <CaretDown size={12} weight="bold"/> : <CaretUp size={12} weight="bold"/>}
              {anyCollapsed ? "expand all" : "collapse all"}
            </button>
          </div>
        </div>
      </div>

      {GROUPS.map((g) => (
        <GroupCard key={g.key} group={g} form={form} onChange={onChange}
          collapsed={collapsedGroups.has(g.key)}
          onToggleCollapse={() => toggleGroup(g.key)}/>
      ))}

      <div className="card-hard rounded-md bg-white p-5 flex items-center justify-between gap-3">
        <p className="font-mono-d text-[11px] text-[var(--brand-muted)]">
          auto-saves as you type · click <b>lock it in</b> when you&apos;re done — it confirms the whole questionnaire and collapses everything.
        </p>
        <button onClick={lockIn} disabled={saving}
          data-testid="eo-lock"
          className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
          <LockKey size={14} weight="bold"/> {saving ? "locking…" : "lock it in"}
        </button>
      </div>
    </section>
  );
}
